import React, { useEffect, useState } from 'react';
import { Download, Share, X } from 'lucide-react';
import { Button } from '../../components/ui/button';

const DISMISS_KEY = 'portal_install_dismissed';

function isStandalone() {
  if (typeof window === 'undefined') return false;
  return (
    window.matchMedia('(display-mode: standalone)').matches
    || window.navigator.standalone === true
  );
}

function isIos() {
  if (typeof navigator === 'undefined') return false;
  return /iphone|ipad|ipod/i.test(navigator.userAgent);
}

/**
 * Banner „Als App installieren“ für Android/Chrome (beforeinstallprompt)
 * und Hinweis für iOS (Teilen → Zum Home-Bildschirm).
 */
export default function PortalInstallPrompt() {
  const [deferred, setDeferred] = useState(null);
  const [showIosHint, setShowIosHint] = useState(false);
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    if (isStandalone()) return undefined;
    try {
      if (localStorage.getItem(DISMISS_KEY) === '1') return undefined;
    } catch (_) { /* */ }

    const onBip = (e) => {
      e.preventDefault();
      setDeferred(e);
      setVisible(true);
    };
    window.addEventListener('beforeinstallprompt', onBip);

    if (isIos()) {
      setShowIosHint(true);
      setVisible(true);
    }

    return () => window.removeEventListener('beforeinstallprompt', onBip);
  }, []);

  const dismiss = () => {
    setVisible(false);
    setDeferred(null);
    try { localStorage.setItem(DISMISS_KEY, '1'); } catch (_) { /* */ }
  };

  const install = async () => {
    if (!deferred) return;
    deferred.prompt();
    try {
      await deferred.userChoice;
    } catch (_) { /* */ }
    setDeferred(null);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="mb-4 rounded-xl border border-brand-red/20 bg-gradient-to-r from-brand-red/5 to-slate-50 px-4 py-3 shadow-sm"
      data-testid="portal-install-prompt"
    >
      <div className="flex items-start gap-3">
        <div className="mt-0.5 flex h-9 w-9 shrink-0 items-center justify-center rounded-lg bg-brand-red text-white">
          <Download className="h-4 w-4" />
        </div>
        <div className="min-w-0 flex-1">
          <p className="text-sm font-semibold text-slate-900">Eltern-Portal als App</p>
          {showIosHint && !deferred ? (
            <p className="mt-0.5 text-xs text-slate-600">
              Tippen Sie auf <Share className="inline h-3.5 w-3.5 text-blue-600" /> Teilen
              und wählen Sie „Zum Home-Bildschirm“.
            </p>
          ) : (
            <p className="mt-0.5 text-xs text-slate-600">
              Installieren Sie das Portal auf dem Homescreen — schneller Zugriff ohne Browser-Leiste.
            </p>
          )}
          <div className="mt-2 flex flex-wrap gap-2">
            {deferred && (
              <Button
                size="sm"
                className="bg-brand-red hover:bg-brand-red/90"
                onClick={install}
                data-testid="portal-install-btn"
              >
                Jetzt installieren
              </Button>
            )}
            <Button size="sm" variant="ghost" onClick={dismiss} data-testid="portal-install-dismiss">
              Später
            </Button>
          </div>
        </div>
        <button
          type="button"
          className="rounded-lg p-1 text-slate-400 hover:bg-white/80 hover:text-slate-600"
          onClick={dismiss}
          aria-label="Schließen"
        >
          <X className="h-4 w-4" />
        </button>
      </div>
    </div>
  );
}
